import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthFetch } from "../hooks/useAuthFetch";
import ConfirmModal from "../utils/ConfirmModal";
import "../stili/DocumentiGenitore.css";

const API_URL = process.env.REACT_APP_API_URL;

const TIPI_DOCUMENTO = [
  "CERTIFICATO_MEDICO",
  "STATO_FAMIGLIA",
  "DOCUMENTO_IDENTITA",
  "ALTRO"
];

export default function DocumentiGenitore() {
  const navigate = useNavigate();
  const authFetch = useAuthFetch();

  const [figli, setFigli] = useState([]);
  const [idGiocatore, setIdGiocatore] = useState("");

  const [tipoDocumento, setTipoDocumento] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);

  const [documenti, setDocumenti] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLista, setLoadingLista] = useState(false);
  const [loadingFigli, setLoadingFigli] = useState(false);

  const [showConfirm, setShowConfirm] = useState(false);
  const [documentoDaEliminare, setDocumentoDaEliminare] = useState(null);

  const formatTipo = (tipo) => {
    switch (tipo) {
      case "CERTIFICATO_MEDICO":
        return "Certificato medico";
      case "STATO_FAMIGLIA":
        return "Stato di famiglia";
      case "DOCUMENTO_IDENTITA":
        return "Documento identità";
      case "ALTRO":
        return "Altro";
      default:
        return tipo;
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === null || bytes === undefined) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatFiglio = (figlio) => {
    const categoria = figlio.categoria ? ` - ${figlio.categoria}` : "";
    return `${figlio.cognome} ${figlio.nome}${categoria}`;
  };

  const resetForm = () => {
    setTipoDocumento("");
    setNote("");
    setFile(null);

    const fileInput = document.getElementById("documento-file-input");
    if (fileInput) fileInput.value = "";
  };

  const fetchFigli = useCallback(async () => {
    try {
      setLoadingFigli(true);
      const res = await authFetch(`${API_URL}/api/genitore/documenti/figli`);
      if (!res.ok) {
        throw new Error("Errore nel recupero figli");
      }

      const data = await res.json().catch(() => []);
      const lista = Array.isArray(data) ? data : [];
      setFigli(lista);

      if (lista.length === 1) {
        setIdGiocatore(String(lista[0].id));
      }
    } catch (err) {
      toast.error(err.message || "Errore durante il caricamento figli");
    } finally {
      setLoadingFigli(false);
    }
  }, [authFetch]);

  const fetchDocumenti = useCallback(async (giocatoreId) => {
    try {
      setLoadingLista(true);

      const query = giocatoreId ? `?idGiocatore=${giocatoreId}` : "";
      const res = await authFetch(`${API_URL}/api/genitore/documenti${query}`);

      if (!res.ok) {
        throw new Error("Errore nel recupero documenti");
      }

      const data = await res.json().catch(() => []);
      setDocumenti(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || "Errore durante il caricamento documenti");
    } finally {
      setLoadingLista(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchFigli();
  }, [fetchFigli]);

  useEffect(() => {
    fetchDocumenti(idGiocatore);
  }, [fetchDocumenti, idGiocatore]);

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!idGiocatore) {
      toast.warning("Seleziona il figlio");
      return;
    }

    if (!tipoDocumento) {
      toast.warning("Seleziona il tipo documento");
      return;
    }

    if (!file) {
      toast.warning("Seleziona un file PDF");
      return;
    }

    if (file.type !== "application/pdf") {
      toast.warning("Puoi caricare solo file PDF");
      return;
    }

    const formData = new FormData();
    formData.append("idGiocatore", idGiocatore);
    formData.append("tipoDocumento", tipoDocumento);
    formData.append("note", note || "");
    formData.append("file", file);

    try {
      setLoading(true);

      const res = await authFetch(`${API_URL}/api/genitore/documenti/upload`, {
        method: "POST",
        body: formData
      });

      const text = await res.text();
      if (!res.ok) {
        throw new Error(text || "Errore durante il caricamento");
      }

      toast.success("Documento caricato con successo");
      resetForm();
      fetchDocumenti(idGiocatore);
    } catch (err) {
      toast.error(err.message || "Errore durante upload documento");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (id, nomeFile) => {
    try {
      const res = await authFetch(`${API_URL}/api/genitore/documenti/${id}/download`);
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Errore durante il download");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nomeFile || "documento.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Errore durante il download");
    }
  };

  const apriConfermaEliminazione = (doc) => {
    setDocumentoDaEliminare(doc);
    setShowConfirm(true);
  };

  const confermaEliminazione = async () => {
    if (!documentoDaEliminare?.id) return;

    try {
      const res = await authFetch(`${API_URL}/api/genitore/documenti/${documentoDaEliminare.id}`, {
        method: "DELETE"
      });

      const text = await res.text();
      if (!res.ok) {
        throw new Error(text || "Errore durante eliminazione");
      }

      toast.success("Documento eliminato con successo");
      setShowConfirm(false);
      setDocumentoDaEliminare(null);
      fetchDocumenti(idGiocatore);
    } catch (err) {
      toast.error(err.message || "Errore durante eliminazione documento");
    }
  };

  const annullaEliminazione = () => {
    setShowConfirm(false);
    setDocumentoDaEliminare(null);
  };

  return (
    <div className="sfondo-immagine">
      <div className="documenti-genitore-page">
        <div className="documenti-genitore-container">
          <h2 className="documenti-genitore-title">Documenti Figlio</h2>

          <form className="documenti-genitore-form" onSubmit={handleUpload}>
            <div className="documenti-genitore-grid">
              <div className="documenti-genitore-field">
                <label>Figlio</label>
                <select
                  value={idGiocatore}
                  onChange={(e) => setIdGiocatore(e.target.value)}
                  disabled={loadingFigli || figli.length === 0}
                >
                  <option value="">
                    {loadingFigli ? "Caricamento figli..." : "Seleziona figlio"}
                  </option>
                  {figli.map((figlio) => (
                    <option key={figlio.id} value={figlio.id}>
                      {formatFiglio(figlio)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="documenti-genitore-field">
                <label>Tipo documento</label>
                <select
                  value={tipoDocumento}
                  onChange={(e) => setTipoDocumento(e.target.value)}
                >
                  <option value="">Seleziona</option>
                  {TIPI_DOCUMENTO.map((tipo) => (
                    <option key={tipo} value={tipo}>
                      {formatTipo(tipo)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="documenti-genitore-field documenti-genitore-field-full">
                <label>File PDF</label>
                <input
                  id="documento-file-input"
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </div>
            </div>

            <div className="documenti-genitore-field">
              <label>Note</label>
              <textarea
                rows="3"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Inserisci eventuali note"
              />
            </div>

            <div className="documenti-genitore-actions">
              <button type="submit" disabled={loading}>
                {loading ? "Caricamento..." : "Carica documento"}
              </button>

              <button
                type="button"
                className="secondary"
                onClick={() => navigate("/gestionale2")}
              >
                Torna al gestionale
              </button>
            </div>
          </form>
        </div>

        <div className="documenti-genitore-container">
          <h3 className="documenti-genitore-subtitle">Documenti caricati</h3>

          {loadingLista ? (
            <p className="documenti-genitore-empty">Caricamento documenti...</p>
          ) : documenti.length === 0 ? (
            <p className="documenti-genitore-empty">Nessun documento caricato.</p>
          ) : (
            <div className="documenti-genitore-list">
              {documenti.map((doc) => (
                <div className="documenti-genitore-card" key={doc.id}>
                  <div className="documenti-genitore-card-main">
                    <div className="documenti-genitore-badge">
                      {formatTipo(doc.tipoDocumento)}
                    </div>

                    <h4>{doc.cognomeGiocatore} {doc.nomeGiocatore}</h4>
                    <p>Documento: {doc.nomeFileOriginale}</p>
                    <p>Categoria: {doc.categoria || "-"}</p>
                    <p>Stato: {doc.statoGiocatore || "-"}</p>
                    <p>Dimensione: {formatBytes(doc.dimensioneFile)}</p>
                    <p>
                      Caricato il:{" "}
                      {doc.dataCaricamento
                        ? new Date(doc.dataCaricamento).toLocaleString("it-IT")
                        : "-"}
                    </p>
                    {doc.note && <p>Note: {doc.note}</p>}
                  </div>

                  <div className="documenti-genitore-card-actions">
                    <button
                      type="button"
                      onClick={() => handleDownload(doc.id, doc.nomeFileOriginale)}
                    >
                      Scarica
                    </button>

                    <button
                      type="button"
                      className="danger"
                      onClick={() => apriConfermaEliminazione(doc)}
                    >
                      Elimina
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <ConfirmModal
            open={showConfirm}
            title="Conferma eliminazione"
            message={
              documentoDaEliminare
                ? `Vuoi eliminare il documento "${documentoDaEliminare.nomeFileOriginale}"?`
                : "Vuoi eliminare questo documento?"
            }
            onConfirm={confermaEliminazione}
            onCancel={annullaEliminazione}
            confirmText="Elimina"
            cancelText="Annulla"
            danger={true}
          />
        </div>
      </div>
    </div>
  );
}